import { Answers, QuestionCodes } from "../../UserJourney/UserJourneyMap";

// Lifestyle screening — seven areas, each coerced to a three-level RAG rating, from which an
// overall rating is derived.
//
// There is no numeric score anywhere in this module by design: the source data is categorical,
// and inventing a number would imply a precision it does not have.
//
// Rating keys are low | moderate | high throughout. The per-area DISPLAY word is "Higher"
// (i18n key ranges.higher) — that is a presentation concern and never a key in this file.

const RATINGS = { low: "low", moderate: "moderate", high: "high" };

// Severity, used both for ordering and for the "at or below" guideline lookup.
const SEVERITY = { low: 0, moderate: 1, high: 2 };

// Declaration order. Also the order the result ring draws its segments.
const LifestyleAreas = ["smoking", "sleep", "diet", "alcohol", "activity", "social", "financial"];

// Clinical tie-break when two areas share a rating. Lower index = surfaced first.
const LifestyleAreaOrder = ["smoking", "alcohol", "diet", "activity", "sleep", "financial", "social"];

const normalise = value =>
    String(value == null ? "" : value)
        .trim()
        .toLowerCase();

// Answers are stored as English text, and pre-September-2023 responses may hold raw i18n keys,
// so compare with includes() rather than equality.
const matches = (value, ...candidates) => {
    const str = normalise(value);
    if (str === "") return false;
    return candidates.some(candidate => {
        const c = normalise(candidate);
        return c !== "" && (str === c || str.includes(c));
    });
};

const { lifestyle: L, SmokingDoYouSmoke: S, AlcoholFrequency: A } = Answers;

// Each rater returns a rating key. Anything unrecognised or unanswered falls through to low — a
// screening survey must never block on a missing answer, and an absent area then contributes
// nothing to the overall rating.
const RATERS = {
    smoking: v => (matches(v, S.yes) ? RATINGS.high : RATINGS.low),

    sleep: v => {
        if (matches(v, L.veryBad)) return RATINGS.high;
        if (matches(v, L.fairlyBad)) return RATINGS.moderate;
        return RATINGS.low;
    },

    // "Fair" is a substring of both "Fairly good" and "Fairly bad", which are sleep answers. The
    // exclusion keeps a mis-routed answer from silently scoring diet as moderate.
    diet: v => {
        if (matches(v, L.poor)) return RATINGS.high;
        if (matches(v, L.fair) && !matches(v, L.fairlyGood, L.fairlyBad)) return RATINGS.moderate;
        return RATINGS.low;
    },

    // Accepts both the current SimpleLocalize spelling ("2-3 times a week") and the hyphen-free
    // form, so a copy edit cannot silently change scoring.
    alcohol: v => {
        if (matches(v, A.fourPlusPerWeek, "4 or more times a week")) return RATINGS.high;
        if (matches(v, A.twoToThreePerWeek, "2 to 3 times a week")) return RATINGS.moderate;
        return RATINGS.low;
    },

    // Average hours of exercise per week over the last 4 weeks. Note that the moderate answer
    // ("1 hour but less than 3 hours") CONTAINS the low answer ("3 hours or more") as a
    // substring, and matches() is substring-based — so the candidates below must stay the full
    // answer strings, and the checks must run most-severe-first.
    activity: v => {
        if (matches(v, L.activityNone, L.activityUnderOneHour)) return RATINGS.high;
        if (matches(v, L.activityOneToThreeHours)) return RATINGS.moderate;
        return RATINGS.low;
    },

    social: v => {
        if (matches(v, L.never, L.rarely)) return RATINGS.high;
        if (matches(v, L.sometimes)) return RATINGS.moderate;
        return RATINGS.low;
    },

    financial: v => {
        if (matches(v, L.often, L.always)) return RATINGS.high;
        if (matches(v, L.sometimes)) return RATINGS.moderate;
        return RATINGS.low;
    }
};

const AREA_QUESTION_CODES = {
    smoking: QuestionCodes.smokingDoYouSmoke,
    sleep: QuestionCodes.subjectiveSleepQuality,
    diet: QuestionCodes.subjectiveDietQuality,
    alcohol: QuestionCodes.alcoholFrequency,
    activity: QuestionCodes.subjectiveActivityHours,
    social: QuestionCodes.socialConnectedness,
    financial: QuestionCodes.subjectiveFinancialSituation
};

// Which guideline thresholds each area actually defines. Smoking is the only asymmetry left, and
// it is a real one rather than a gap in the copy: the question has no middle answer, so smoking
// never rates moderate. Everything else carries advice at both bands.
//
// The "most severe at or below" rule below is therefore near-identity now, but it stays: it is
// what keeps guidelineRatingFor total when an area defines fewer bands than it can rate.
const LifestyleGuidelineDefinition = {
    smoking: [RATINGS.high],
    sleep: [RATINGS.moderate, RATINGS.high],
    diet: [RATINGS.moderate, RATINGS.high],
    alcohol: [RATINGS.moderate, RATINGS.high],
    activity: [RATINGS.moderate, RATINGS.high],
    social: [RATINGS.moderate, RATINGS.high],
    financial: [RATINGS.moderate, RATINGS.high]
};

/**
 * The most severe guideline defined at or below the given rating, or null when the area defines
 * none at or below it.
 *
 * - sleep @ high       -> "high"      (both defined; the more severe wins)
 * - diet @ moderate    -> "moderate"  (both defined; nothing more severe at or below)
 * - smoking @ high     -> "high"      (the only band smoking defines)
 * - anything @ low     -> null        (no area defines advice at low)
 *
 * @param {string} area
 * @param {string} rating
 * @returns {string|null}
 */
const guidelineRatingFor = (area, rating) => {
    const defined = LifestyleGuidelineDefinition[area] || [];
    const applicable = defined.filter(d => SEVERITY[d] <= SEVERITY[rating]);
    if (applicable.length === 0) return null;
    return applicable.reduce((worst, d) => (SEVERITY[d] > SEVERITY[worst] ? d : worst));
};

/**
 * Overall rating from the set of area ratings.
 *
 * The source specification is neither total nor disjoint — it leaves "2 moderate, 0 high"
 * unrated (that fails "<= 1 moderate", and 2 is not "> 2"), and "2 high" matches both moderate
 * and high. This reads "> 2 moderate" as ">= 2 moderate" and evaluates most-severe-first, which
 * makes the rule total, disjoint and gap-free.
 *
 * @param {string[]} ratings
 * @returns {string}
 */
const overallRatingFor = ratings => {
    const highCount = ratings.filter(r => r === RATINGS.high).length;
    const moderateCount = ratings.filter(r => r === RATINGS.moderate).length;

    if (highCount >= 2) return RATINGS.high;
    if (highCount === 1 || moderateCount >= 2) return RATINGS.moderate;
    return RATINGS.low;
};

/**
 * Score a lifestyle response.
 *
 * @param {Object} answers - question code -> answer value
 * @returns {{ overallRating: string, areas: Array<{area: string, rating: string, guidelineRating: string|null}> }}
 */
function lifestyle(answers = {}) {
    const areas = LifestyleAreas.map(area => {
        const rating = RATERS[area](answers[AREA_QUESTION_CODES[area]]);
        return { area, rating, guidelineRating: guidelineRatingFor(area, rating) };
    });

    const overallRating = overallRatingFor(areas.map(a => a.rating));

    // Worst first, then clinical priority. Sorting a copy keeps LifestyleAreas order stable for
    // callers that want the declaration order (the ring's segment layout).
    const ordered = [...areas].sort((a, b) => {
        const bySeverity = SEVERITY[b.rating] - SEVERITY[a.rating];
        if (bySeverity !== 0) return bySeverity;
        return LifestyleAreaOrder.indexOf(a.area) - LifestyleAreaOrder.indexOf(b.area);
    });

    return { overallRating, areas: ordered };
}

const RiskName = "Lifestyle";
const RiskCode = "LIFESTYLE";
const GuidelineName = "SiSU Lifestyle Guideline";
const GuidelineCode = "SISU_LIFESTYLE";

export {
    lifestyle,
    LifestyleAreas,
    LifestyleAreaOrder,
    LifestyleGuidelineDefinition,
    guidelineRatingFor,
    overallRatingFor,
    RiskName,
    RiskCode,
    GuidelineName,
    GuidelineCode
};
